/**
 * PulseFilterPanel — the home dashboard's full-overlay filter view (design:
 * gripp-os `FilterPanel`, packages/ui/src/components/filter-panel.tsx).
 *
 * Opened from the header filter button, it covers the dashboard body (map + card
 * strip) with its own header row — a back chevron + "Filter (N)" title (N = the
 * active, non-default selection count) and a reset control — over a stack of pill
 * sections plus an in-panel search box that narrows which pills are shown
 * (view-only, mirroring the other filter panels' FilterContent search).
 *
 * Two section shapes, mirroring the rest of the app's filter panels:
 *  - `multi`  (ACTIVITY TYPES): an "All" pill + a multi-select of coloured-dot
 *    pills. "All" = nothing else selected.
 *  - `meta`   (GROUPS / LABELS): the All / "(not grouped)" / "(not labeled)" +
 *    items model from shared-ui MetaFilterPills, so the home panel reads exactly
 *    like the Activity/Asset panels.
 *
 * Pure presentation: every selection is page-owned (useHomeDashboardFilters), so
 * the panel, the map and the card strip all read/write one source — the panel
 * just renders the section models and fires their callbacks. The in-panel search
 * query is the one piece of local state (it never touches the server filter).
 * Web-only — do NOT import from mobile code.
 */
import {
  ArrowLeftIcon,
  Colors,
  DotIcon,
  FilterChip,
  FilterSectionTitle,
  ResetIcon,
  SearchInput,
  tokens,
} from '@gripp/shared-ui';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

export interface FilterOption {
  id: string;
  label: string;
  /** Leading colour dot (activity-type swatches). Omit for plain text pills. */
  dotColor?: string;
}

/** ACTIVITY TYPES: an "All" pill + a multi-select of coloured-dot pills. */
export interface MultiFilterSection {
  kind: 'multi';
  id: string;
  label: string;
  allLabel: string;
  /** Selectable options (excludes the leading "All" pill). */
  options: FilterOption[];
  /** Selected option ids ([] = "All"). */
  selected: string[];
  onToggleOption: (optionId: string) => void;
  onSelectAll: () => void;
}

/**
 * GROUPS / LABELS: the All / None / items meta-pill model shared with the other
 * filter panels (see shared-ui MetaFilterPills). `includeAll` drives the "All"
 * pill; `includeNone` drives the "(not grouped)" / "(not labeled)" pill.
 */
export interface MetaFilterSection {
  kind: 'meta';
  id: string;
  label: string;
  allLabel: string;
  noneLabel: string;
  includeAll: boolean;
  includeNone: boolean;
  items: FilterOption[];
  selectedItems: string[];
  onToggleAll: () => void;
  onToggleNone: () => void;
  onToggleItem: (id: string) => void;
}

export type FilterSectionModel = MultiFilterSection | MetaFilterSection;

interface PulseFilterPanelProps {
  sections: FilterSectionModel[];
  /** Active (non-default) selection count — drives the header title number. */
  activeCount: number;
  onReset: () => void;
  onClose: () => void;
}

// View-only search match: empty/whitespace query matches everything.
const matches = (label: string, query: string) => {
  const q = query.trim().toLowerCase();
  return q === '' || label.toLowerCase().includes(q);
};

export const PulseFilterPanel = ({
  sections,
  activeCount,
  onReset,
  onClose,
}: PulseFilterPanelProps) => {
  const { t } = useTranslation();
  const [backHovered, setBackHovered] = useState(false);
  const [resetHovered, setResetHovered] = useState(false);
  // In-panel search — narrows which pills/sections are shown. Local only; it
  // never touches the page filter state or the map.
  const [searchQuery, setSearchQuery] = useState('');

  // Build the visible pill set per section (filtered by the search query). A
  // section renders only when at least one of its pills matches — mirroring the
  // other panels' FilterContent.
  const visibleSections = sections
    .map((section) => renderSection(section, searchQuery))
    .filter((s) => s.chips.length > 0);

  return (
    <View style={styles.root} testID="pulse-dashboard-filter-panel">
      {/* Header row — mirrors the dashboard top bar (56px, white, bottom border).
          Left: back chevron + "Filter (N)". Right: reset. */}
      <View style={styles.header}>
        <Pressable
          style={styles.headerBack}
          onPress={onClose}
          onHoverIn={() => setBackHovered(true)}
          onHoverOut={() => setBackHovered(false)}
          accessibilityRole="button"
          accessibilityLabel={t('home.filters.close', 'Close filters')}
          testID="pulse-dashboard-filter-back"
        >
          <ArrowLeftIcon
            size={20}
            color={
              backHovered
                ? Colors.role.textPrimary
                : Colors.role.textStrong
            }
          />
          <Text style={styles.headerTitle}>
            {t('home.filters.label', 'Filter')}
          </Text>
          <Text style={styles.headerCount}>({activeCount})</Text>
        </Pressable>

        <Pressable
          style={styles.headerReset}
          onPress={onReset}
          onHoverIn={() => setResetHovered(true)}
          onHoverOut={() => setResetHovered(false)}
          disabled={activeCount === 0}
          accessibilityRole="button"
          accessibilityLabel={t('home.filters.reset', 'Reset filters')}
          accessibilityState={{ disabled: activeCount === 0 }}
          testID="pulse-dashboard-filter-reset"
        >
          <ResetIcon
            size={20}
            color={
              activeCount === 0
                ? Colors.role.surfaceInverseSoftActive
                : resetHovered
                ? Colors.role.textPrimary
                : Colors.role.textStrong
            }
          />
        </Pressable>
      </View>

      {/* In-panel search — view-only chip filter (matches the other panels). */}
      <View style={styles.searchHeader}>
        <SearchInput
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder={t('common.filterSearch.placeholder', 'Search filters')}
          width="100%"
          height={40}
          testID="pulse-dashboard-filter-search"
        />
      </View>

      {/* Category sections — vertical stack of labelled pill rows. */}
      <ScrollView
        style={styles.body}
        contentContainerStyle={styles.bodyContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {visibleSections.map((section) => (
          <View key={section.id}>
            <FilterSectionTitle>{section.label}</FilterSectionTitle>
            <View style={styles.pillRow}>{section.chips}</View>
          </View>
        ))}

        {/* Empty state — search active but nothing matches. */}
        {searchQuery.trim() !== '' && visibleSections.length === 0 && (
          <View style={styles.emptyState}>
            <Text style={styles.emptyStateText}>
              {t('common.filterSearch.noResults', {
                query: searchQuery.trim(),
                defaultValue: `No filters match "${searchQuery.trim()}"`,
              })}
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

/**
 * Builds the visible pill list for one section, filtered by the search query.
 * Returns the section's id/label plus the rendered chips (empty when nothing in
 * the section matches, so the caller can drop the whole section).
 */
function renderSection(
  section: FilterSectionModel,
  searchQuery: string
): { id: string; label: string; chips: ReactElement[] } {
  const chips: ReactElement[] = [];

  if (section.kind === 'multi') {
    if (matches(section.allLabel, searchQuery)) {
      chips.push(
        <FilterChip
          key={`${section.id}-all`}
          label={section.allLabel}
          selected={section.selected.length === 0}
          onPress={section.onSelectAll}
          testID={`pulse-filter-pill-${section.id}-all`}
        />
      );
    }
    section.options
      .filter((option) => matches(option.label, searchQuery))
      .forEach((option) =>
        chips.push(
          <FilterChip
            key={option.id}
            label={option.label}
            selected={section.selected.includes(option.id)}
            onPress={() => section.onToggleOption(option.id)}
            leading={
              option.dotColor ? (
                <DotIcon size={8} color={option.dotColor} />
              ) : undefined
            }
            testID={`pulse-filter-pill-${option.id}`}
          />
        )
      );
  } else {
    if (matches(section.allLabel, searchQuery)) {
      chips.push(
        <FilterChip
          key={`${section.id}-all`}
          label={section.allLabel}
          selected={section.includeAll}
          onPress={section.onToggleAll}
          testID={`pulse-filter-pill-${section.id}-all`}
        />
      );
    }
    if (matches(section.noneLabel, searchQuery)) {
      chips.push(
        <FilterChip
          key={`${section.id}-none`}
          label={section.noneLabel}
          selected={section.includeNone}
          onPress={section.onToggleNone}
          testID={`pulse-filter-pill-${section.id}-none`}
        />
      );
    }
    section.items
      .filter((item) => matches(item.label, searchQuery))
      .forEach((item) =>
        chips.push(
          <FilterChip
            key={item.id}
            label={item.label}
            selected={section.selectedItems.includes(item.id)}
            onPress={() => section.onToggleItem(item.id)}
            testID={`pulse-filter-pill-${item.id}`}
          />
        )
      );
  }

  return { id: section.id, label: section.label, chips };
}

const styles = StyleSheet.create({
  root: {
    // Fills its half-width column in the dashboard split (see PulseDashboard).
    flex: 1,
    backgroundColor: Colors.role.surface,
    borderLeftWidth: 1,
    borderLeftColor: Colors.role.borderSection,
  },
  header: {
    height: tokens.spacing['7xl'], // 56px — matches the dashboard top bar
    paddingHorizontal: tokens.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Colors.role.surface,
    borderBottomWidth: 1,
    borderBottomColor: Colors.role.borderSection,
  },
  headerBack: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  headerTitle: {
    ...tokens.typography.title.small,
    fontWeight: '700',
    color: Colors.role.textStrong,
  },
  headerCount: {
    ...tokens.typography.title.small,
    color: Colors.role.textPrimary,
  },
  headerReset: {
    width: 36,
    height: 36,
    borderRadius: tokens.borderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchHeader: {
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.lg,
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    padding: tokens.spacing.lg,
    gap: tokens.spacing['2xl'],
  },
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
  },
  emptyState: {
    paddingTop: tokens.spacing['2xl'],
    alignItems: 'center',
  },
  emptyStateText: {
    ...tokens.typography.body.medium,
    color: Colors.role.textSecondary,
    textAlign: 'center',
  },
});
